import { compact, every, flatten, last } from 'lodash';

import {
  AppDelinquentInterestSettingFragment,
  Attribution,
  SharedAppDelinquentInterestSettingFragment,
  SharedAppPaymentDueFragment,
} from '@willow/graphql-iso/src/app';
import { PortalDelinquentInterestSettingFragment } from '@willow/graphql-iso/src/portal';
import { add } from '@willow/shared-iso';
import { DelinquentInterestSettingId, parseGqlIsoDate } from '@willow/types-iso';

export type DelinquentInterestAttribution = Attribution & { fulfilled: boolean };

export const getDelinquentInterestAttributions = (
  paymentsDue: SharedAppPaymentDueFragment[],
  delinquentInterestSettingId: DelinquentInterestSettingId,
): DelinquentInterestAttribution[] =>
  flatten(
    compact(
      paymentsDue.map((pd) => {
        const dqlAttribution = pd.attribution.find(
          (a) =>
            a.__typename === 'DelinquentInterestAttribution' &&
            (a.delinquentInterestSettingId == null || // TODO remove this once we have this on all DI attributions
              (a.delinquentInterestSettingId != null && a.delinquentInterestSettingId === delinquentInterestSettingId)),
        );
        if (dqlAttribution) {
          return {
            ...dqlAttribution,
            fulfilled: pd.status.fulfilled,
          };
        }
        return undefined;
      }),
    ),
  );

export const isDelinquentInterestFulfilled = (attributions: DelinquentInterestAttribution[]): boolean =>
  every(attributions, (a) => a.fulfilled);

export const getTotalDelinquentInterest = (
  paymentsDue: SharedAppPaymentDueFragment[],
  delinquentInterestSettingId: DelinquentInterestSettingId,
): number => {
  const attributions = getDelinquentInterestAttributions(paymentsDue, delinquentInterestSettingId);

  return add(...attributions.map((a) => (a.isWaived ? 0 : a.interest)));
};

export const getDelinquentInterestSettingStatus = (
  delinquentInterestSetting: SharedAppDelinquentInterestSettingFragment | PortalDelinquentInterestSettingFragment,
  paymentsDue: SharedAppPaymentDueFragment[],
  delinquentInterestSettingId: DelinquentInterestSettingId,
): string => {
  const attributions = getDelinquentInterestAttributions(paymentsDue, delinquentInterestSettingId);

  return delinquentInterestSetting.isWaived
    ? 'Waived'
    : delinquentInterestSetting.isDeferred
      ? 'Deferred'
      : isDelinquentInterestFulfilled(attributions) && delinquentInterestSetting.endDate
        ? 'Paid'
        : !delinquentInterestSetting.endDate
          ? 'Charging'
          : delinquentInterestSetting.endDate
            ? 'Charged'
            : 'N/A';
};

export const getActiveDelinquentInterestSetting = (
  delinquentInterestSettings: AppDelinquentInterestSettingFragment[],
  today: Date = new Date(),
): AppDelinquentInterestSettingFragment | undefined => {
  return last(
    delinquentInterestSettings.filter(
      (dis) => !dis.isWaived && (dis.endDate == null || (dis.endDate && parseGqlIsoDate(dis.endDate) > today)),
    ),
  );
};

export const getActivePortalDelinquentInterestSetting = (
  delinquentInterestSettings: PortalDelinquentInterestSettingFragment[],
  today: Date = new Date(),
): PortalDelinquentInterestSettingFragment | undefined => {
  return last(
    delinquentInterestSettings.filter(
      (dis) => !dis.isWaived && (dis.endDate == null || (dis.endDate && parseGqlIsoDate(dis.endDate) > today)),
    ),
  );
};
